import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://eventshowcase.de';

const cookieTypes = [
  {
    id: 'essential',
    name: 'Essential Cookies',
    icon: 'ri-lock-line',
    alwaysOn: true,
    description: 'These cookies are strictly necessary for the website to function. They cannot be disabled.',
    examples: [
      { name: 'supabase-auth-token', purpose: 'Keeps you logged in during your session', duration: 'Session', provider: 'Supabase' },
      { name: 'es_cookie_consent', purpose: 'Stores your cookie consent preferences', duration: '1 year', provider: 'EVENTSHOWCASE' },
      { name: '__cf_bm', purpose: 'Cloudflare bot management and security', duration: '30 minutes', provider: 'Cloudflare' },
    ],
  },
  {
    id: 'analytics',
    name: 'Analytics Cookies',
    icon: 'ri-bar-chart-line',
    alwaysOn: false,
    description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.',
    examples: [
      { name: '_ga', purpose: 'Distinguishes unique users for Google Analytics', duration: '2 years', provider: 'Google Analytics' },
      { name: '_ga_*', purpose: 'Stores session state for Google Analytics 4', duration: '2 years', provider: 'Google Analytics' },
      { name: '_gid', purpose: 'Distinguishes users for Google Analytics (short-term)', duration: '24 hours', provider: 'Google Analytics' },
      { name: '_gat', purpose: 'Throttles request rate to Google Analytics', duration: '1 minute', provider: 'Google Analytics' },
    ],
  },
  {
    id: 'advertising',
    name: 'Advertising & Targeting Cookies',
    icon: 'ri-advertisement-line',
    alwaysOn: false,
    description: 'These cookies are set by Google AdSense and its partners to build a profile of your interests and show you relevant ads on other sites.',
    examples: [
      { name: 'IDE', purpose: 'Used by Google DoubleClick to register and report user actions after viewing or clicking an ad', duration: '1 year', provider: 'Google DoubleClick' },
      { name: 'test_cookie', purpose: 'Checks if the user\'s browser supports cookies', duration: '15 minutes', provider: 'Google DoubleClick' },
      { name: 'DSID', purpose: 'Identifies a signed-in Google user to remember ad preferences', duration: '2 weeks', provider: 'Google' },
      { name: 'FLC', purpose: 'Records a specific ad click and stores information about it', duration: '10 seconds', provider: 'Google' },
      { name: 'AID', purpose: 'Links user activity across Google-owned products for ad targeting', duration: '13 months', provider: 'Google' },
      { name: 'TAID', purpose: 'Links user activity across devices for ad targeting', duration: '14 days', provider: 'Google' },
    ],
  },
  {
    id: 'functional',
    name: 'Functional Cookies',
    icon: 'ri-settings-3-line',
    alwaysOn: false,
    description: 'These cookies enable enhanced functionality and personalisation, such as remembering your preferences and settings.',
    examples: [
      { name: 'i18n_lang', purpose: 'Remembers your preferred language setting', duration: '1 year', provider: 'EVENTSHOWCASE' },
      { name: 'theme_pref', purpose: 'Stores your display preference (light/dark)', duration: '1 year', provider: 'EVENTSHOWCASE' },
    ],
  },
];

const sections = [
  {
    id: 'what-are-cookies',
    title: '1. What Are Cookies?',
    content: `Cookies are small text files that are placed on your device (computer, smartphone, or tablet) when you visit a website. They are widely used to make websites work more efficiently and to provide information to website owners.

Cookies allow websites to recognise your device and remember certain information about your visit, such as your preferences and login status. They can also be used to track your browsing behaviour across different websites for advertising purposes.

Cookies are not harmful — they cannot execute programs or deliver viruses to your computer. However, they do store information about your browsing activity, which is why we ask for your consent before placing non-essential cookies on your device.`,
  },
  {
    id: 'how-we-use',
    title: '2. How We Use Cookies',
    content: `EVENTSHOWCASE uses cookies for the following purposes:

**Authentication & Security** — To keep you logged in and protect your account from unauthorised access.

**Preferences** — To remember your settings and preferences so you don't have to re-enter them on every visit.

**Analytics** — To understand how visitors use our website, which pages are most popular, and how we can improve the user experience. We use Google Analytics for this purpose.

**Advertising** — To display relevant advertisements through Google AdSense. These ads help fund the free features of our platform. Google and its partners use cookies to serve ads based on your prior visits to our website and other websites.

**Performance** — To monitor the performance of our website and identify any technical issues.`,
  },
  {
    id: 'third-party',
    title: '3. Third-Party Cookies',
    content: `Some cookies on our website are set by third-party services. We do not control these cookies and they are subject to the privacy policies of the respective third parties.

**Google Analytics (analytics.google.com)**
We use Google Analytics to analyse website traffic and user behaviour. Google Analytics uses cookies to collect information about how visitors use our site. This information is used to compile reports and help us improve the website. Google may also transfer this information to third parties where required to do so by law, or where such third parties process the information on Google's behalf.
Opt-out: https://tools.google.com/dlpage/gaoptout

**Google AdSense (google.com/adsense)**
We use Google AdSense to display advertisements. Google AdSense uses cookies to serve ads based on your prior visits to our website and other websites. Google's use of advertising cookies enables it and its partners to serve ads based on your visit to our site and/or other sites on the internet.
Opt-out: https://www.google.com/settings/ads

**Supabase (supabase.com)**
We use Supabase for authentication and database services. Supabase sets cookies to maintain your login session securely.

**Cloudflare (cloudflare.com)**
Our website is protected by Cloudflare, which sets cookies for security and performance purposes.`,
  },
  {
    id: 'managing-cookies',
    title: '4. Managing Your Cookie Preferences',
    content: `You have several options for managing cookies:

**Cookie Consent Banner**
When you first visit our website, you will see a cookie consent banner. You can choose to accept all cookies, decline non-essential cookies, or customise your preferences by clicking "Manage". Your choice is saved for 1 year.

**Browser Settings**
Most web browsers allow you to control cookies through their settings. You can set your browser to refuse all cookies, accept only certain cookies, or notify you when a cookie is being set. Please note that if you disable cookies, some features of our website may not function properly.

Browser cookie settings guides:
- Chrome: chrome://settings/cookies
- Firefox: about:preferences#privacy
- Safari: Preferences > Privacy
- Edge: edge://settings/privacy

**Google Opt-Out Tools**
- Google Analytics: https://tools.google.com/dlpage/gaoptout
- Google Ads: https://www.google.com/settings/ads
- Network Advertising Initiative: http://www.networkadvertising.org/managing/opt_out.asp
- AboutAds: https://www.aboutads.info/choices/

**Do Not Track**
Some browsers have a "Do Not Track" feature that signals to websites that you do not want to be tracked. Our website currently does not respond to Do Not Track signals, but you can use the opt-out tools listed above.`,
  },
  {
    id: 'consent',
    title: '5. Your Consent',
    content: `By clicking "Accept All" on our cookie consent banner, you consent to our use of all cookies described in this policy, including analytics and advertising cookies.

By clicking "Decline", you consent only to essential cookies that are strictly necessary for the website to function.

By clicking "Manage", you can choose which categories of non-essential cookies you accept.

You can withdraw or change your consent at any time by clearing your browser cookies and revisiting our website, which will trigger the consent banner again.

For users in the European Economic Area (EEA), we comply with the EU ePrivacy Directive and GDPR requirements for cookie consent. We do not place non-essential cookies before obtaining your consent.`,
  },
  {
    id: 'updates',
    title: '6. Updates to This Policy',
    content: `We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any significant changes by updating the "Last Updated" date at the top of this page.

We encourage you to review this Cookie Policy periodically to stay informed about how we use cookies.`,
  },
  {
    id: 'contact',
    title: '7. Contact Us',
    content: `If you have any questions about our use of cookies, please contact us:

**EVENTSHOWCASE**
Website: https://eventshowcase.de
Email: privacy@eventshowcase.de

For GDPR-related enquiries, please include "Cookie Policy" in the subject line.`,
  },
];

export default function CookiePolicyPage() {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [expandedType, setExpandedType] = useState<string | null>('essential');

  useEffect(() => {
    const PAGE_TITLE = 'Cookie Policy – EVENTSHOWCASE';
    const PAGE_DESC = 'Read the EVENTSHOWCASE Cookie Policy. Learn about the cookies we use, including Google Analytics and Google AdSense cookies, and how to manage your preferences.';
    const PAGE_URL = `${SITE_URL}/legal/cookies`;

    document.title = PAGE_TITLE;

    const setMeta = (sel: string, attr: string, val: string) => {
      let el = document.querySelector(sel) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        const [a, v] = sel.replace('meta[', '').replace(']', '').split('=');
        el.setAttribute(a.trim(), v.replace(/"/g, '').trim());
        document.head.appendChild(el);
        el.setAttribute('data-page-meta', 'true');
      }
      el.setAttribute(attr, val);
    };
    setMeta('meta[name="description"]', 'content', PAGE_DESC);
    setMeta('meta[name="robots"]', 'content', 'index, follow');
    setMeta('meta[property="og:title"]', 'content', PAGE_TITLE);
    setMeta('meta[property="og:description"]', 'content', PAGE_DESC);
    setMeta('meta[property="og:url"]', 'content', PAGE_URL);
    setMeta('meta[property="og:type"]', 'content', 'website');

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Cookie Policy – EVENTSHOWCASE',
      description: PAGE_DESC,
      url: PAGE_URL,
      dateModified: '2026-04-05',
      isPartOf: { '@type': 'WebSite', name: 'EVENTSHOWCASE', url: SITE_URL },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Legal', item: `${SITE_URL}/legal` },
          { '@type': 'ListItem', position: 3, name: 'Cookie Policy', item: PAGE_URL },
        ],
      },
    };

    const existing = document.getElementById('schema-cookies');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'schema-cookies';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('schema-cookies');
      if (el) el.remove();
      document.querySelectorAll('meta[data-page-meta="true"]').forEach(e => e.remove());
    };
  }, []);

  const renderContent = (content: string) => {
    return content.split('\n\n').map((paragraph, i) => {
      const parts = paragraph.split(/(\*\*[^*]+\*\*)/g);
      return (
        <p key={i} className="text-gray-600 text-sm leading-relaxed mb-3">
          {parts.map((part, j) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={j} className="text-gray-800 font-semibold">{part.slice(2, -2)}</strong>;
            }
            return part;
          })}
        </p>
      );
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/">
            <img
              src="https://public.readdy.ai/ai/img_res/35af317c-ff06-4831-b241-787dd1236a773.png"
              alt="EVENTSHOWCASE"
              className="h-8 w-auto"
            />
          </Link>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <Link to="/legal/privacy-policy" className="hover:text-gray-900 transition-colors">Privacy Policy</Link>
            <Link to="/legal/terms" className="hover:text-gray-900 transition-colors">Terms</Link>
            <Link to="/" className="hover:text-gray-900 transition-colors">Home</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-gray-950 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-medium px-3 py-1.5 rounded-full mb-6">
            <i className="ri-file-text-line text-sm"></i>
            Legal Document
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4 font-['Playfair_Display']">Cookie Policy</h1>
          <p className="text-gray-400 text-sm sm:text-base max-w-2xl">
            This policy explains what cookies are, how EVENTSHOWCASE uses them, and how you can manage your preferences — including cookies used by Google AdSense.
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-6 text-xs text-gray-500">
            <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i> Last Updated: April 5, 2026</span>
            <span className="flex items-center gap-1.5"><i className="ri-global-line"></i> Applies to: eventshowcase.de</span>
            <span className="flex items-center gap-1.5"><i className="ri-shield-check-line"></i> ePrivacy & GDPR Compliant</span>
          </div>
        </div>
      </div>

      {/* Cookie Types Overview */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Cookie Categories We Use</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {cookieTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => setExpandedType(expandedType === type.id ? null : type.id)}
                className={`text-left p-4 rounded-xl border transition-all cursor-pointer ${
                  expandedType === type.id
                    ? 'border-gray-900 bg-white'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg">
                    <i className={`${type.icon} text-gray-600 text-sm`}></i>
                  </div>
                  {type.alwaysOn ? (
                    <span className="text-[10px] font-bold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">Always On</span>
                  ) : (
                    <span className="text-[10px] font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Optional</span>
                  )}
                </div>
                <p className="text-xs font-semibold text-gray-800">{type.name}</p>
                <p className="text-[11px] text-gray-500 mt-1 leading-relaxed line-clamp-2">{type.description}</p>
              </button>
            ))}
          </div>

          {/* Expanded cookie table */}
          {expandedType && (
            <div className="mt-4 bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-800">
                  {cookieTypes.find(t => t.id === expandedType)?.name} — Details
                </p>
                <button onClick={() => setExpandedType(null)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Cookie Name</th>
                      <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Purpose</th>
                      <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Duration</th>
                      <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Provider</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cookieTypes.find(t => t.id === expandedType)?.examples.map((ex, i) => (
                      <tr key={i} className="border-b border-gray-50 last:border-0">
                        <td className="px-4 py-3 font-mono text-gray-700 font-medium">{ex.name}</td>
                        <td className="px-4 py-3 text-gray-500 max-w-xs">{ex.purpose}</td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{ex.duration}</td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{ex.provider}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12 sm:py-16">
        <div className="flex flex-col lg:flex-row gap-10">

          {/* Table of Contents */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="lg:sticky lg:top-24">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Contents</p>
              <nav className="space-y-1">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    onClick={() => setActiveSection(section.id)}
                    className={`block text-xs py-1.5 px-3 rounded-md transition-colors cursor-pointer ${
                      activeSection === section.id
                        ? 'bg-gray-900 text-white font-medium'
                        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {section.title}
                  </a>
                ))}
              </nav>

              <div className="mt-8 p-4 bg-gray-50 rounded-xl border border-gray-100">
                <p className="text-xs font-semibold text-gray-700 mb-2">Opt-Out Links</p>
                <div className="space-y-2">
                  {[
                    { label: 'Google Ads Settings', url: 'https://www.google.com/settings/ads' },
                    { label: 'GA Opt-Out', url: 'https://tools.google.com/dlpage/gaoptout' },
                    { label: 'NAI Opt-Out', url: 'http://www.networkadvertising.org/managing/opt_out.asp' },
                    { label: 'AboutAds.info', url: 'https://www.aboutads.info/choices/' },
                  ].map((link, i) => (
                    <a
                      key={i}
                      href={link.url}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-900 transition-colors cursor-pointer"
                    >
                      <i className="ri-external-link-line text-xs"></i>
                      {link.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* Policy Sections */}
          <main className="flex-1 min-w-0">
            <div className="space-y-10">
              {sections.map((section) => (
                <section key={section.id} id={section.id} className="scroll-mt-24">
                  <h2 className="text-base font-bold text-gray-900 mb-4 pb-3 border-b border-gray-100">
                    {section.title}
                  </h2>
                  <div>{renderContent(section.content)}</div>
                </section>
              ))}
            </div>

            {/* Footer note */}
            <div className="mt-12 p-6 bg-gray-50 rounded-xl border border-gray-100">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-gray-500 text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800 mb-1">Questions about cookies?</p>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Contact us at{' '}
                    <a href="mailto:privacy@eventshowcase.de" className="text-gray-800 underline hover:no-underline">
                      privacy@eventshowcase.de
                    </a>. We aim to respond within 5 business days.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Link to="/legal/privacy-policy" className="flex items-center gap-3 p-4 border border-gray-100 rounded-xl hover:border-gray-300 transition-colors cursor-pointer group">
                <div className="w-9 h-9 flex items-center justify-center bg-gray-100 rounded-lg group-hover:bg-gray-200 transition-colors flex-shrink-0">
                  <i className="ri-shield-check-line text-gray-600"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-gray-800">Privacy Policy</p>
                  <p className="text-xs text-gray-400">Data handling</p>
                </div>
              </Link>
              <Link to="/legal/terms" className="flex items-center gap-3 p-4 border border-gray-100 rounded-xl hover:border-gray-300 transition-colors cursor-pointer group">
                <div className="w-9 h-9 flex items-center justify-center bg-gray-100 rounded-lg group-hover:bg-gray-200 transition-colors flex-shrink-0">
                  <i className="ri-file-text-line text-gray-600"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-gray-800">Terms of Service</p>
                  <p className="text-xs text-gray-400">Usage rules</p>
                </div>
              </Link>
              <Link to="/" className="flex items-center gap-3 p-4 border border-gray-100 rounded-xl hover:border-gray-300 transition-colors cursor-pointer group">
                <div className="w-9 h-9 flex items-center justify-center bg-gray-100 rounded-lg group-hover:bg-gray-200 transition-colors flex-shrink-0">
                  <i className="ri-home-4-line text-gray-600"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-gray-800">Back to Home</p>
                  <p className="text-xs text-gray-400">EVENTSHOWCASE</p>
                </div>
              </Link>
            </div>
          </main>
        </div>
      </div>

      {/* Footer strip */}
      <div className="border-t border-gray-100 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-400">
          <p>&copy; 2026 EVENTSHOWCASE. All rights reserved.</p>
          <div className="flex items-center gap-3">
            <Link to="/legal/cookies" className="text-gray-700 font-medium">Cookie Policy</Link>
            <span>·</span>
            <Link to="/legal/privacy-policy" className="hover:text-gray-700 transition-colors">Privacy Policy</Link>
            <span>·</span>
            <Link to="/legal/terms" className="hover:text-gray-700 transition-colors">Terms</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
